// Copyright (c) 2026 Optimum Healthcare IT. All rights reserved.
// Author: Frank Scalzo <Optimum Healthcare IT>
// License: proprietary
// provenance-id: 5f8805df-ffa3-47f9-bebc-be6a508e47d1

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Typography,
  Stack, MenuItem, TextField, Alert, AlertTitle, LinearProgress, Chip, Table,
  TableHead, TableRow, TableCell, TableBody, Divider, Tooltip, Checkbox,
  FormControlLabel, Collapse,
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import BlockIcon from '@mui/icons-material/Block';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import HistoryIcon from '@mui/icons-material/History';
import * as XLSX from 'xlsx';
import {
  allFieldsFor, FIELD_TYPES, normalizeHeader, resolveHeader, suggestExistingField,
  duplicateValueReport,
} from './schema';
import {
  normalizeKey, IDENTITY_STRATEGIES, strategyFor, planImport, collisionReport,
  identityStamp, fingerprint, findRunByFingerprint, recordRun, listRuns,
} from './importIdentity';
import { detectTagColumns, parseRowTags, summarizeTags, mergeTags } from './importTags';
import store, { beginPersistBatch, endPersistBatch } from './store';
import HelpBubble from './HelpBubble';

// Import applications from a CSV/Excel export (LeanIX, ServiceNow, a discovery
// questionnaire, or any inventory).
//
// Clients never send us files whose columns match our field names, so every
// incoming header is resolved against the ONE canonical alias registry in
// schema.js (resolveHeader / normalizeHeader). This file deliberately does NOT
// keep an alias table of its own: it used to, and because three aliases in it
// each claimed two canonical fields, a .find() silently picked a winner and
// wrote ~700 values twice under two different names across the loaded clients.
// Two alias tables in two files is exactly the duplication we are here to stop.
//
// The second job of this dialog is to stop custom-field proliferation. An
// unrecognised header used to become cf_<header> automatically, which fragments
// the common field set one client at a time. Now every unrecognised column is a
// decision the user makes, with near-miss suggestions from the existing schema
// offered first, and the number of NEW fields shown before the import runs.
//
// The third job is idempotency: a client sends the same inventory again — a
// corrected export, "v3 FINAL", the same file twice — and a re-import must
// UPDATE the applications that already exist rather than duplicate the estate.
// That is importIdentity.js, and it is used here in four places:
//   * the user chooses ONCE what makes two rows the same record, and the
//     collision report for that choice is shown BEFORE it can be used, because
//     a strategy that merges two different applications is worse than a
//     duplicate (see the 58 measured DNS/name collisions in that file);
//   * planImport dry-runs the whole file against the workspace, so the exact
//     create / update / unchanged / skipped counts are on screen and confirmed
//     before a single record is written;
//   * the uploaded bytes are fingerprinted, so re-uploading a file that was
//     already imported says so, with the date and the counts;
//   * every record written carries its identity key, so the next upload still
//     matches it after the client renames it.
// Matching normalises with normalizeKey (whitespace-collapsed), not
// .toLowerCase() — trailing spaces are routine in spreadsheet exports and used
// to create a second copy of an application that was already there.

const TYPE = 'Application';
const NEW_FIELD = '__new__';   // deliberately create a custom field
const SKIP_COL = '__skip__';   // deliberately exclude the column
const cfKeyFor = (header) => `cf_${normalizeHeader(header)}`;

// Auto-map headers through the canonical registry. Anything that does not
// resolve is left undecided ('') so the user chooses; nothing is invented and
// nothing is dropped behind their back.
function autoMap(headers, selectableKeys) {
  const map = {};
  const ambiguous = {};
  headers.forEach((h) => {
    const res = resolveHeader(h, TYPE);
    if (res.ambiguous) { map[h] = ''; ambiguous[h] = res.ambiguous; return; }
    if (res.key && selectableKeys.has(res.key)) { map[h] = res.key; return; }
    map[h] = ''; // unresolved -> needs a decision
  });
  return { map, ambiguous };
}

export default function ImportDialog({ open, onClose, tenantId, onImported }) {
  // The master workspace is a cross-client view, not a data workspace.
  const isMasterWorkspace = tenantId === 'optimum';
  const fileRef = useRef(null);
  const [rows, setRows] = useState([]);
  const [headers, setHeaders] = useState([]);
  const [mapping, setMapping] = useState({});
  const [ambiguous, setAmbiguous] = useState({});
  const [sheetNames, setSheetNames] = useState([]);
  const [workbook, setWorkbook] = useState(null);
  const [fileName, setFileName] = useState('');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);
  const [existingDefs, setExistingDefs] = useState([]);
  const [showBlank, setShowBlank] = useState(false);
  const [done, setDone] = useState(null);
  // Source-cloud tag columns (Azure "Tags", AWS "Tag:Key", …) parsed into real
  // Primordia tags. Additive: the raw columns still map/import exactly as
  // before. Off = exactly today's behavior.
  const [importSourceTags, setImportSourceTags] = useState(true);

  // --- identity / idempotency -------------------------------------------
  const [existingSheets, setExistingSheets] = useState([]);
  const [strategy, setStrategy] = useState('name');
  const [identityCol, setIdentityCol] = useState('');
  const [ackCollisions, setAckCollisions] = useState(false);
  const [armed, setArmed] = useState(false);          // plan reviewed, write allowed
  const [fileHash, setFileHash] = useState('');
  const [priorRun, setPriorRun] = useState(null);     // this exact file, imported before
  const [runs, setRuns] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [expand, setExpand] = useState({});

  // Every field an import can write. LIFECYCLE-typed fields are timeline dates
  // the importer does not populate, so they are not offered as targets.
  const fields = useMemo(
    () => allFieldsFor(TYPE).filter((f) => f.type !== FIELD_TYPES.LIFECYCLE),
    [],
  );
  const fieldByKey = useMemo(() => new Map(fields.map((f) => [f.key, f])), [fields]);
  const labelFor = useCallback(
    (key) => (fieldByKey.get(key) ? fieldByKey.get(key).label : key),
    [fieldByKey],
  );

  // Custom fields this tenant already has — needed to warn about a cf_ key that
  // already exists under a different label (same key, two meanings).
  useEffect(() => {
    if (!open) return undefined;
    let alive = true;
    store.listFieldDefs(tenantId, TYPE)
      .then((defs) => { if (alive) setExistingDefs(defs || []); })
      .catch(() => { if (alive) setExistingDefs([]); });
    return () => { alive = false; };
  }, [open, tenantId]);

  // What is already in this workspace — the dry-run plan is computed against it,
  // and it is what makes a re-import an UPDATE instead of a second copy.
  //
  // Guarded on the master workspace: listFactSheets('optimum', …) returns every
  // tenant's sheets, so the name match behind "this is an UPDATE" could pick a
  // DIFFERENT client's application and write into it. Belt and braces with the
  // banner above — if this dialog is ever opened from somewhere else, it still
  // must not load another client's records to match against.
  useEffect(() => {
    if (!open) return undefined;
    if (isMasterWorkspace) { setExistingSheets([]); return undefined; }
    let alive = true;
    store.listFactSheets(tenantId, { type: TYPE })
      .then((list) => { if (alive) setExistingSheets(list || []); })
      .catch(() => { if (alive) setExistingSheets([]); });
    setRuns(listRuns(tenantId));
    return () => { alive = false; };
  }, [open, tenantId, isMasterWorkspace]);

  const readSheet = (wb, sheetName) => {
    const ws = wb.Sheets[sheetName];
    const raw = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' });
    // Header = the first row with the most non-empty cells (handles title rows).
    let hIdx = 0, best = 0;
    for (let i = 0; i < Math.min(raw.length, 15); i++) {
      const c = (raw[i] || []).filter((x) => String(x).trim()).length;
      if (c > best) { best = c; hIdx = i; }
    }
    // The header list and the row keys have to be generated ONE way, or they
    // disagree and every lookup on the difference misses silently:
    //
    //   * headers were trimmed while `sheet_to_json` keyed rows by the raw cell
    //     text, so a header typed as "Owner " mapped a column that could never
    //     be read back (M9);
    //   * duplicate headers were kept verbatim while `sheet_to_json` suffixes
    //     repeats ("Owner", "Owner_1"), so two columns collapsed onto one
    //     mapping entry, the second was never read, never counted as undecided,
    //     and emitted a duplicate React key (N4).
    //
    // Build the rows positionally from the same array the headers come from.
    // The names below are then the only names in play.
    const seenHdr = new Map();
    const canonical = (raw[hIdx] || []).map((h) => {
      const t = String(h).trim();
      if (!t) return '';
      const n = seenHdr.get(t) || 0;
      seenHdr.set(t, n + 1);
      return n ? `${t}_${n}` : t;   // matches how a repeat reads to the user
    });
    const hdrs = canonical.filter(Boolean);
    const data = raw.slice(hIdx + 1).map((r) => {
      const o = {};
      canonical.forEach((k, i) => {
        if (!k) return;
        const v = (r || [])[i];
        o[k] = v === undefined || v === null ? '' : v;
      });
      return o;
    });
    const auto = autoMap(hdrs, new Set(fields.map((f) => f.key)));
    setHeaders(hdrs);
    setRows(data.filter((r) => Object.values(r).some((v) => String(v).trim())));
    setMapping(auto.map);
    setAmbiguous(auto.ambiguous);
    setShowBlank(false);
    setIdentityCol('');
  };

  const onFile = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    setErr(''); setDone(null); setPriorRun(null); setFileHash('');
    try {
      const buf = await file.arrayBuffer();
      // Hash a copy of the bytes before anything else touches them: same file
      // re-uploaded => same fingerprint, even under a different filename.
      const hash = await fingerprint(buf.slice(0));
      const wb = XLSX.read(buf, { type: 'array' });
      setFileHash(hash);
      setPriorRun(findRunByFingerprint(tenantId, hash));
      setWorkbook(wb); setFileName(file.name); setSheetNames(wb.SheetNames);
      readSheet(wb, wb.SheetNames[0]);
    } catch (e2) {
      setErr('Could not read that file. Supported: .xlsx, .xls, .csv');
    }
  };

  const setTarget = (header, value) => setMapping((m) => ({ ...m, [header]: value }));

  // Near-miss candidates for a header, limited to fields an import can write.
  const suggestions = useMemo(() => {
    const out = {};
    headers.forEach((h) => {
      out[h] = suggestExistingField(h, TYPE).filter((s) => fieldByKey.has(s.key));
    });
    return out;
  }, [headers, fieldByKey]);

  // How much data each column actually carries. A column mapped to a canonical
  // field but empty in every row has NOT populated that field, and we never
  // report it as if it had.
  const columnStats = useMemo(() => {
    const out = {};
    headers.forEach((h) => {
      let filled = 0;
      let sample = '';
      rows.forEach((r) => {
        const v = String(r[h] ?? '').trim();
        if (v) { filled += 1; if (!sample) sample = v; }
      });
      out[h] = { filled, sample };
    });
    return out;
  }, [headers, rows]);

  const existingDefByKey = useMemo(
    () => new Map((existingDefs || []).map((d) => [d.key, d])),
    [existingDefs],
  );

  // Label for any field key, including custom fields this tenant already has.
  const anyLabel = useCallback((key) => {
    if (fieldByKey.has(key)) return fieldByKey.get(key).label;
    const def = existingDefByKey.get(key);
    return def ? def.label : key;
  }, [fieldByKey, existingDefByKey]);

  // The common-field-set picture for this file.
  const summary = useMemo(() => {
    const supplied = new Set();
    const mappedEmpty = new Set();
    const newCols = [];
    const skipped = [];
    const undecided = [];
    const targetCount = new Map();

    headers.forEach((h) => {
      const t = mapping[h];
      if (t === NEW_FIELD) { newCols.push(h); return; }
      if (t === SKIP_COL) { skipped.push(h); return; }
      if (!t) { undecided.push(h); return; }
      targetCount.set(t, (targetCount.get(t) || 0) + 1);
      if ((columnStats[h] || {}).filled > 0) supplied.add(t); else mappedEmpty.add(t);
    });
    mappedEmpty.forEach((k) => { if (supplied.has(k)) mappedEmpty.delete(k); });

    // Two columns aimed at one canonical field: the first non-empty value wins,
    // so say so rather than letting one column quietly overwrite the other.
    const collisions = [...targetCount.entries()]
      .filter(([, n]) => n > 1)
      .map(([k]) => k);

    // cf_ key clashes: against fields this tenant already has, and against other
    // columns in this same file that normalise to the same key.
    const cfLabels = new Map();
    const cfClashes = [];
    newCols.forEach((h) => {
      const key = cfKeyFor(h);
      const prior = existingDefByKey.get(key);
      if (prior && String(prior.label).trim() !== String(h).trim()) {
        cfClashes.push({ header: h, key, other: prior.label, where: 'already on this workspace' });
      }
      const twin = cfLabels.get(key);
      if (twin && twin !== h) {
        cfClashes.push({ header: h, key, other: twin, where: 'another column in this file' });
      } else if (!twin) {
        cfLabels.set(key, h);
      }
    });

    return {
      supplied,
      mappedEmpty,
      blank: fields.filter((f) => !supplied.has(f.key)),
      newCols,
      skipped,
      undecided,
      collisions,
      cfClashes,
    };
  }, [headers, mapping, columnStats, fields, existingDefByKey]);

  // ---- source-cloud tag columns ------------------------------------------
  // Detection over the (canonicalised) header list; the summary is exact over
  // every row — only the rendered group list is capped at 8.
  const tagCols = useMemo(() => detectTagColumns(headers), [headers]);
  const tagSummary = useMemo(
    () => ((tagCols.columns.length || tagCols.reserved.length) ? summarizeTags(rows, tagCols) : null),
    [rows, tagCols],
  );

  const nameCol = Object.entries(mapping).find(([, v]) => v === 'name');
  const nameHeader = nameCol ? nameCol[0] : '';
  const mappedCount = Object.values(mapping)
    .filter((v) => v && v !== NEW_FIELD && v !== SKIP_COL).length;
  const ambiguousHeaders = Object.keys(ambiguous).filter((h) => !mapping[h]);
  const blockers = summary.undecided.length;

  // ---- dry run -----------------------------------------------------------
  // The exact field values a row would write. Used for the plan AND for the
  // write, so the plan cannot describe something different from what happens.
  // lifecycleState is NOT defaulted here: forcing 'Active' onto every row would
  // make every existing record look changed when it is not.
  const valuesFor = useCallback((row) => {
    const out = {};
    Object.entries(mapping).forEach(([h, target]) => {
      if (!target || target === SKIP_COL) return;
      const val = String(row[h] ?? '').trim();
      if (!val) return;
      const key = target === NEW_FIELD ? cfKeyFor(h) : target;
      if (out[key]) return; // first non-empty column wins; never overwrite
      out[key] = val;
    });
    return out;
  }, [mapping]);

  const strategyDef = strategyFor(strategy);
  const needsIdentityCol = strategy !== 'name';

  // Rows in the shape importIdentity understands: a name, the identity value
  // from the column the user pointed at, and the values this row would write.
  const planRows = useMemo(() => {
    if (!nameHeader) return [];
    const idOf = (row) => (identityCol ? String(row[identityCol] ?? '').trim() : '');
    return rows.map((row, i) => ({
      i,
      raw: row,
      name: String(row[nameHeader] ?? '').trim(),
      values: valuesFor(row),
      sourceId: strategy === 'sourceId' ? idOf(row) : '',
      dnsName: (strategy === 'dnsName' || strategy === 'dnsShort') ? idOf(row) : '',
    }));
  }, [rows, nameHeader, identityCol, strategy, valuesFor]);

  const keyCollisions = useMemo(
    () => collisionReport(planRows, strategy),
    [planRows, strategy],
  );

  const plan = useMemo(
    () => planImport(planRows, existingSheets, { strategy, allowNameFallback: true }),
    [planRows, existingSheets, strategy],
  );

  // Any change to the file, the mapping or the identity choice invalidates the
  // reviewed plan. Nothing is ever written against a stale plan.
  useEffect(() => { setArmed(false); }, [mapping, rows, strategy, identityCol, fileHash]);
  useEffect(() => { setAckCollisions(false); }, [rows, strategy, identityCol]);

  const collisionBlocked = keyCollisions.collisions.length > 0 && !ackCollisions;
  const identityMissing = needsIdentityCol && !identityCol;
  const canWrite = !!nameCol && !blockers && !collisionBlocked && !identityMissing
    && (plan.create.length + plan.update.length + plan.unchanged.length) > 0;

  const toggleExpand = (k) => setExpand((e) => ({ ...e, [k]: !e[k] }));

  // Reasons rows would be skipped, grouped — a skipped row is never hidden.
  const skipReasons = useMemo(() => {
    const m = new Map();
    plan.skipped.forEach((s) => m.set(s.reason, (m.get(s.reason) || 0) + 1));
    return [...m.entries()];
  }, [plan]);

  const createAllRemaining = () => {
    setMapping((m) => {
      const next = { ...m };
      headers.forEach((h) => { if (!next[h]) next[h] = NEW_FIELD; });
      return next;
    });
  };

  // Does this record still carry the identity we are about to match it on? An
  // unchanged record still needs its key recorded, or the NEXT upload — after
  // the client renames it — would create a duplicate.
  const stampDiffers = useCallback((prior, pr) => {
    const stamp = identityStamp(pr, strategy, fileName);
    const f = { ...(prior && prior.fields) };
    return Object.keys(stamp).some(
      (k) => String(f[k] == null ? '' : f[k]) !== String(stamp[k] == null ? '' : stamp[k]),
    );
  }, [strategy, fileName]);

  const runImport = async () => {
    if (!nameCol) { setErr('Map one column to "Name" — it identifies each application.'); return; }
    if (blockers) {
      setErr(`${blockers} column${blockers === 1 ? '' : 's'} still need a decision: map to an existing field, create a new custom field, or exclude it.`);
      return;
    }
    if (identityMissing) {
      setErr(`Choose the column that carries the ${strategyDef.label} — that strategy cannot match anything without it.`);
      return;
    }
    if (collisionBlocked) {
      setErr('This identity strategy would merge records that are not the same thing. Acknowledge the collisions or choose another strategy.');
      return;
    }
    setErr(''); setBusy(true); setProgress(0);

    // Counters live out here so a failure part-way through can still report
    // exactly what was written before it stopped.
    let created = 0, updated = 0, stamped = 0;
    let noLifecycle = 0;
    let tasks = [];
    let doneCount = 0;

    // Batch this workspace's writes: H8's remaining cost was one localStorage
    // rewrite per row. Every store call below still updates the in-memory
    // cache immediately (so re-plan, progress and the result panel all see the
    // latest rows) — this only defers the actual persist to one write at the
    // end, in the `finally` below, so it still runs even if the import stops
    // partway through.
    beginPersistBatch(tenantId);
    try {
    const existing = await store.listFactSheets(tenantId, { type: TYPE });

    // Re-plan against what is in the workspace RIGHT NOW. If it no longer
    // matches the plan that was on screen, write nothing and show the new one.
    const fresh = planImport(planRows, existing, { strategy, allowNameFallback: true });
    if (fresh.create.length !== plan.create.length || fresh.update.length !== plan.update.length) {
      setExistingSheets(existing); setArmed(false);
      setErr('The workspace changed since this plan was calculated, so nothing was written. The plan below has been recalculated — review the new counts and confirm again.');
      return;
    }

    // Register custom fields only for columns the user deliberately chose, and
    // only once per key.
    const createdKeys = new Set();
    for (const h of summary.newCols) {
      const key = cfKeyFor(h);
      if (createdKeys.has(key) || existingDefByKey.has(key)) continue;
      createdKeys.add(key);
      await store.addFieldDef(tenantId, TYPE, { key, label: h, type: FIELD_TYPES.TEXT });
    }

    const restamp = fresh.unchanged.filter((e) => stampDiffers(e.prior, e.row));
    tasks = [
      ...fresh.create.map((e) => ({ kind: 'create', e })),
      ...fresh.update.map((e) => ({ kind: 'update', e })),
      ...restamp.map((e) => ({ kind: 'stamp', e })),
    ];

    for (let i = 0; i < tasks.length; i++) {
      const { kind, e } = tasks[i];
      const pr = e.row;
      const stamp = identityStamp(pr, strategy, fileName);
      // pr.raw is the row exactly as read from the sheet — the same object the
      // "Tags detected" summary was computed from.
      const rowTags = importSourceTags ? parseRowTags(pr.raw, tagCols) : [];

      if (kind === 'create') {
        const name = pr.name || pr.values.name || e.key;
        const values = { ...pr.values, ...stamp };
        if (!values.name) values.name = name;
        // A missing lifecycle used to be stamped 'Active'. A spreadsheet that
        // never mentioned lifecycle is not evidence that every row is in
        // service — it inflated the Dashboard's active count and the Landscape
        // grouping by exactly the number of blank rows, with nothing on screen
        // saying a value had been invented. Unknown is a state the product
        // already renders ("(not set)" in the Inventory facets), so leave it
        // unknown and report how many.
        if (!values.lifecycleState) { values.lifecycleState = ''; noLifecycle += 1; }
        // One write, not two. createFactSheet saves, and the follow-up save
        // rewrote the whole database a second time for every created row;
        // saveFactSheet assigns the id when there isn't one.
        await store.saveFactSheet({
          type: TYPE,
          tenantId,
          name,
          fields: { name, ...values },
          tags: rowTags,
          relations: {},
          quality: 'Draft',
        });
        created += 1;
      } else if (kind === 'update') {
        // Tags are MERGED, never replaced — tags somebody set by hand survive
        // a re-import. mergeTags copies; e.prior's frozen array is untouched.
        await store.saveFactSheet({
          ...e.prior,
          tags: mergeTags(e.prior.tags, rowTags),
          fields: { ...e.prior.fields, ...pr.values, ...stamp },
        });
        updated += 1;
      } else {
        // Nothing in the data changed — record the identity only, and never
        // report this row as an update.
        await store.saveFactSheet({ ...e.prior, fields: { ...e.prior.fields, ...stamp } });
        stamped += 1;
      }
      doneCount = i + 1;
      if (i % 5 === 0) {
        setProgress(Math.round((i / Math.max(tasks.length, 1)) * 100));
        // Yield to the event loop, or React never paints between iterations and
        // the progress bar jumps from 0 to 100 on a run long enough to matter.
        // eslint-disable-next-line no-await-in-loop
        await new Promise((r) => setTimeout(r, 0));
      }
    }
    setProgress(100);

    setDone({
      created,
      updated,
      noLifecycle,
      unchanged: fresh.unchanged.length,
      stamped,
      skipped: fresh.skipped,
      strategy,
      newFields: createdKeys.size,
      excluded: summary.skipped.length,
      // Pre-existing residue of the old ambiguous aliases. Reported, never
      // merged — collapsing two client fields into one is an admin decision.
      residue: duplicateValueReport(existing).filter((r) => r.identical > 0),
    });

    recordRun(tenantId, {
      file: fileName,
      fingerprint: fileHash,
      strategy,
      identityColumn: identityCol,
      rows: planRows.length,
      created,
      updated,
      unchanged: fresh.unchanged.length,
      skipped: fresh.skipped.length,
    });
    setRuns(listRuns(tenantId));
    setArmed(false);

    store.listFieldDefs(tenantId, TYPE).then((defs) => setExistingDefs(defs || [])).catch(() => {});
    store.listFactSheets(tenantId, { type: TYPE })
      .then((list) => setExistingSheets(list || [])).catch(() => {});
    if (onImported) onImported();
    } catch (e) {
      // Without this, one rejected save left `busy` true forever: the dialog
      // sat at "Importing 42%" with no error, and `onClose` is suppressed while
      // busy, so it could not even be closed. An import is not a transaction —
      // rows already written stay written — so say exactly how far it got
      // rather than implying nothing happened.
      const at = doneCount + 1;
      setErr(`Import stopped on row ${at} of ${tasks.length}: ${(e && e.message) || 'a write failed'}. `
        + `${created + updated + stamped} record(s) were already written and remain — the counts below are what landed. `
        + 'Re-running the same file is safe: matched records update in place rather than duplicating.');
      setDone({
        created,
        updated,
        noLifecycle,
        stamped,
        failedAt: at,
        totalTasks: tasks.length,
        unchanged: 0,
        skipped: [],
        strategy,
        newFields: 0,
        excluded: 0,
        residue: [],
      });
      setArmed(false);
      store.listFactSheets(tenantId, { type: TYPE })
        .then((list) => setExistingSheets(list || [])).catch(() => {});
      if (onImported) onImported();
    } finally {
      endPersistBatch(tenantId);
      setBusy(false);
    }
  };

  const reset = () => {
    setRows([]); setHeaders([]); setMapping({}); setAmbiguous({}); setWorkbook(null);
    setSheetNames([]); setFileName(''); setErr(''); setDone(null); setProgress(0);
    setFileHash(''); setPriorRun(null); setIdentityCol(''); setAckCollisions(false);
    setArmed(false); setExpand({});
  };

  // One big number per outcome. The counts are the point of the whole screen,
  // so they are readable from across a room and never rounded or merged.
  const planStat = (k, label, n, color, expandable) => (
    <Box key={k} sx={{
      minWidth: 132, flex: '1 1 132px', p: 1, borderRadius: 1,
      border: '1px solid', borderColor: 'divider', bgcolor: 'background.paper', textAlign: 'center',
    }}>
      <Typography variant="h4" sx={{ fontWeight: 800, color, lineHeight: 1.1 }}>{n}</Typography>
      <Typography variant="caption" sx={{ color: '#5C6B7A', display: 'block', fontWeight: 600 }}>
        {label}
      </Typography>
      {expandable && n > 0 && (
        <Button size="small" onClick={() => toggleExpand(k)} sx={{ textTransform: 'none' }}>
          {expand[k] ? 'hide' : 'show'} list
        </Button>
      )}
    </Box>
  );

  const CAP = 200; // render cap only — every row is still counted and imported
  const renderPlanRows = (entries, kind) => (
    <Box sx={{ mt: 1, maxHeight: 240, overflowY: 'auto', border: '1px solid #e3e9ef', borderRadius: 1 }}>
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow sx={{ '& th': { fontWeight: 700, color: 'sectionHeader.main' } }}>
            <TableCell>Row</TableCell>
            <TableCell>Name in file</TableCell>
            <TableCell>{kind === 'skipped' ? 'Why it is skipped' : 'What happens'}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {entries.slice(0, CAP).map((e) => {
            const pr = e.row;
            const renamed = e.prior && normalizeKey(e.prior.name) !== normalizeKey(pr.name);
            return (
              <TableRow key={`${kind}-${e.i}`}>
                <TableCell sx={{ color: '#5C6B7A' }}>{e.i + 2}</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{pr.name || <em>(blank)</em>}</TableCell>
                <TableCell sx={{ color: '#5C6B7A' }}>
                  {kind === 'create' && `new application — identity recorded as “${e.key || normalizeKey(pr.name)}”`}
                  {kind === 'skipped' && e.reason}
                  {kind === 'unchanged' && `already matches the record found by ${e.matchedBy} — no values differ, nothing is rewritten`}
                  {kind === 'update' && (
                    <>
                      matched by {e.matchedBy};{' '}
                      {e.changed.length} field{e.changed.length === 1 ? '' : 's'} change:{' '}
                      {e.changed.slice(0, 8).map(anyLabel).join(', ')}
                      {e.changed.length > 8 ? ` +${e.changed.length - 8} more` : ''}
                      {renamed && ` — renames “${e.prior.name}”`}
                    </>
                  )}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
      {entries.length > CAP && (
        <Typography variant="caption" sx={{ display: 'block', p: 1, color: '#5C6B7A' }}>
          Showing the first {CAP} of {entries.length}. All {entries.length} are included in the counts
          above and in the import.
        </Typography>
      )}
    </Box>
  );

  const renderHistory = () => (
    <Box sx={{ mt: 2 }}>
      <Button size="small" startIcon={<HistoryIcon />} onClick={() => setShowHistory((v) => !v)}
        sx={{ textTransform: 'none', color: '#5C6B7A' }}>
        {showHistory ? 'Hide' : 'Show'} import history for {tenantId} ({runs.length})
      </Button>
      <Collapse in={showHistory}>
        {runs.length === 0 ? (
          <Typography variant="caption" sx={{ color: '#5C6B7A', display: 'block', mt: 0.5 }}>
            No imports recorded for this workspace yet.
          </Typography>
        ) : (
          <Table size="small" sx={{ mt: 1 }}>
            <TableHead>
              <TableRow sx={{ '& th': { fontWeight: 700, color: 'sectionHeader.main' } }}>
                <TableCell>File</TableCell>
                <TableCell>When</TableCell>
                <TableCell>Matched on</TableCell>
                <TableCell>Result</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {runs.map((r, idx) => (
                <TableRow key={`${r.fingerprint || 'nohash'}-${r.at}-${idx}`}>
                  <TableCell sx={{ fontWeight: 600 }}>{r.file}</TableCell>
                  <TableCell sx={{ color: '#5C6B7A' }}>
                    {r.at ? new Date(r.at).toLocaleString() : ''}
                  </TableCell>
                  <TableCell sx={{ color: '#5C6B7A' }}>
                    {strategyFor(r.strategy).label}
                    {r.identityColumn ? ` (${r.identityColumn})` : ''}
                  </TableCell>
                  <TableCell sx={{ color: '#5C6B7A' }}>
                    {r.created} created, {r.updated} updated, {r.unchanged || 0} unchanged
                    {r.skipped ? `, ${r.skipped} skipped` : ''}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Collapse>
    </Box>
  );

  const renderTargetCell = (h) => {
    const target = mapping[h] || '';
    const sugg = suggestions[h] || [];
    const amb = ambiguous[h];
    const extraKey = target && target !== NEW_FIELD && target !== SKIP_COL && !fieldByKey.has(target)
      ? target : null;

    return (
      <Stack spacing={0.75}>
        <TextField
          select size="small" fullWidth value={target}
          onChange={(e) => setTarget(h, e.target.value)}
        >
          <MenuItem value=""><em>needs a decision</em></MenuItem>
          <MenuItem value={NEW_FIELD}>Create NEW custom field ({cfKeyFor(h)})</MenuItem>
          <MenuItem value={SKIP_COL}>Do not import this column</MenuItem>
          <Divider />
          {extraKey && <MenuItem value={extraKey}>{extraKey}</MenuItem>}
          {fields.map((f) => (
            <MenuItem key={f.key} value={f.key}>
              {f.label}
              <span style={{ color: '#9AA6B2', marginLeft: 6 }}>({f.section})</span>
            </MenuItem>
          ))}
        </TextField>

        {amb && (
          <Alert severity="error" sx={{ py: 0 }}>
            <Typography variant="caption" sx={{ fontWeight: 700 }}>
              This header is claimed by more than one field — choose one, we will not guess:
            </Typography>
            <Stack direction="row" spacing={0.5} sx={{ mt: 0.5, flexWrap: 'wrap', gap: 0.5 }}>
              {amb.map((k) => (
                <Button key={k} size="small" variant="outlined" color="error"
                  onClick={() => setTarget(h, k)}>
                  {labelFor(k)}
                </Button>
              ))}
            </Stack>
          </Alert>
        )}

        {!target && !amb && sugg.length > 0 && (
          <Box>
            <Typography variant="caption" sx={{ color: '#5C6B7A' }}>
              Looks like an existing field — map to it instead of creating a new one:
            </Typography>
            <Stack direction="row" sx={{ mt: 0.5, flexWrap: 'wrap', gap: 0.5 }}>
              {sugg.map((s) => (
                <Button
                  key={s.key} size="small" variant="contained" startIcon={<ArrowForwardIcon />}
                  onClick={() => setTarget(h, s.key)}
                  sx={{ bgcolor: '#0D5B9A', textTransform: 'none', '&:hover': { bgcolor: '#021F4B' } }}
                >
                  {s.label}
                </Button>
              ))}
            </Stack>
          </Box>
        )}

        {!target && !amb && (
          <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 0.5 }}>
            <Button size="small" variant="text" startIcon={<AddCircleOutlineIcon />}
              onClick={() => setTarget(h, NEW_FIELD)}
              sx={{ color: '#5C6B7A', textTransform: 'none' }}>
              Create new field
            </Button>
            <Button size="small" variant="text" startIcon={<BlockIcon />}
              onClick={() => setTarget(h, SKIP_COL)}
              sx={{ color: '#5C6B7A', textTransform: 'none' }}>
              Exclude column
            </Button>
          </Stack>
        )}

        {target === NEW_FIELD && (
          <Stack direction="row" alignItems="center" sx={{ flexWrap: 'wrap', gap: 0.5 }}>
            <Chip size="small" color="warning" variant="outlined"
              label={`NEW custom field: ${cfKeyFor(h)}`} />
            {sugg.slice(0, 2).map((s) => (
              <Button key={s.key} size="small" variant="outlined"
                onClick={() => setTarget(h, s.key)}
                sx={{ textTransform: 'none', borderColor: '#0D5B9A', color: '#0D5B9A' }}>
                map to {s.label} instead
              </Button>
            ))}
          </Stack>
        )}

        {target === SKIP_COL && (
          <Typography variant="caption" sx={{ color: '#5C6B7A' }}>
            Excluded on purpose — this column will not be imported.
          </Typography>
        )}
      </Stack>
    );
  };

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} fullWidth maxWidth="md">
      <DialogTitle sx={{ fontWeight: 700, color: 'sectionHeader.main' }}>
        Import applications from a file <HelpBubble topic="importDialog" />
      </DialogTitle>
      <DialogContent dividers>
        {/*
          Importing from the master workspace has no correct outcome. New rows
          are refused by the API (the master workspace holds no records), and
          matched rows are worse: `listFactSheets('optimum', …)` fans out across
          EVERY tenant, so a name match could land on a different client's
          application and the import would write into their record. Two clients
          both running "Epic" is the ordinary case, not a corner case.

          There is no way to infer which client a spreadsheet row belongs to, so
          this asks rather than guesses.
        */}
        {isMasterWorkspace ? (
          <Alert severity="info">
            <AlertTitle sx={{ fontWeight: 700 }}>Choose a client workspace first</AlertTitle>
            <Typography variant="body2">
              The master workspace is a cross-client view and holds no records of
              its own, so there is nothing here to import into. Switch to the
              client this file belongs to and run the import there — that way each
              row is matched only against that client&apos;s own applications.
            </Typography>
          </Alert>
        ) : (
        <>
        {err && <Alert severity="error" sx={{ mb: 2 }}>{err}</Alert>}
        {done && (
          <Alert severity={done.failedAt ? 'warning' : 'success'} sx={{ mb: 2 }}>
            <AlertTitle sx={{ fontWeight: 700 }}>
              {done.failedAt
                ? `Stopped part-way into ${tenantId} on row ${done.failedAt} of ${done.totalTasks}: `
                : `Imported into ${tenantId}: `}
              {done.created} created, {done.updated} updated,
              {' '}{done.unchanged} unchanged, {done.skipped.length} skipped.
            </AlertTitle>
            {done.noLifecycle > 0 && (
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {done.noLifecycle} created record{done.noLifecycle === 1 ? '' : 's'} had no lifecycle in the file, so
                lifecycle was left unset rather than assumed. They will not appear in the Dashboard&apos;s active count
                until somebody sets one.
              </Typography>
            )}
            <Typography variant="body2">
              Matched on {strategyFor(done.strategy).label}. {done.unchanged} record
              {done.unchanged === 1 ? '' : 's'} held identical values and
              {done.stamped
                ? ` were not rewritten — ${done.stamped} had their identity key recorded so the next upload still matches them`
                : ' were not rewritten'}
              . {done.newFields} new custom field{done.newFields === 1 ? '' : 's'} created
              {done.excluded ? `, ${done.excluded} column(s) deliberately excluded` : ''}.
            </Typography>
            {done.skipped.length > 0 && (
              <Box sx={{ mt: 0.5, maxHeight: 120, overflowY: 'auto' }}>
                {done.skipped.slice(0, 50).map((s) => (
                  <Typography key={`skip-${s.i}`} variant="caption" sx={{ display: 'block', color: '#5C6B7A' }}>
                    Row {s.i + 2} “{s.row.name || '(blank)'}” skipped — {s.reason}.
                  </Typography>
                ))}
                {done.skipped.length > 50 && (
                  <Typography variant="caption" sx={{ display: 'block', color: '#5C6B7A' }}>
                    …and {done.skipped.length - 50} more skipped rows.
                  </Typography>
                )}
              </Box>
            )}
            {done.residue.map((r) => (
              <Typography key={`${r.a}-${r.b}`} variant="caption" sx={{ display: 'block', color: '#5C6B7A' }}>
                Existing data note: {r.identical} of {r.both} applications hold the same value in
                {' '}{labelFor(r.a)} and {labelFor(r.b)} — residue of an older import. Nothing was
                merged; review it in the workspace.
              </Typography>
            ))}
          </Alert>
        )}

        {!rows.length ? (
          <Box
            onClick={() => fileRef.current && fileRef.current.click()}
            sx={{
              border: '2px dashed #cfd8dc', borderRadius: 2, p: 5, textAlign: 'center',
              cursor: 'pointer', '&:hover': { borderColor: '#0D5B9A', bgcolor: 'background.paper' },
            }}
          >
            <UploadFileIcon sx={{ fontSize: 44, color: '#0D5B9A' }} />
            <Typography sx={{ fontWeight: 600, mt: 1 }}>Choose a spreadsheet</Typography>
            <Typography variant="body2" sx={{ color: '#5C6B7A' }}>
              .xlsx, .xls or .csv — a LeanIX export, ServiceNow extract, or a discovery questionnaire
            </Typography>
            <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" hidden onChange={onFile} />
          </Box>
        ) : (
          <>
            <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}>
              <Chip label={fileName} onDelete={busy ? undefined : reset} />
              <Chip color="primary" label={`${rows.length} rows`} />
              <Chip label={`${mappedCount}/${headers.length} columns auto-mapped to common fields`} />
              {sheetNames.length > 1 && (
                <TextField select size="small" label="Sheet" defaultValue={sheetNames[0]}
                  onChange={(e) => readSheet(workbook, e.target.value)} sx={{ minWidth: 200 }}>
                  {sheetNames.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                </TextField>
              )}
              <Box sx={{ flex: 1 }} />
              <Chip variant="outlined" label={`into: ${tenantId}`} />
            </Stack>

            {/* Same bytes, already loaded. Not blocked — just impossible to do
                by accident. */}
            {priorRun && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                <AlertTitle sx={{ fontWeight: 700 }}>
                  This is the same file you imported on {priorRun.at ? new Date(priorRun.at).toLocaleString() : 'an earlier date'}
                </AlertTitle>
                <Typography variant="body2">
                  Uploaded then as “{priorRun.file}” and matched on {strategyFor(priorRun.strategy).label};
                  that run created {priorRun.created} and updated {priorRun.updated}.
                  {' '}Re-importing it will update {plan.update.length} record
                  {plan.update.length === 1 ? '' : 's'} and create {plan.create.length}
                  {plan.create.length === 0 ? ' — no duplicates' : ''}.
                  {' '}Go ahead if that is what you want; the plan below is the truth of it.
                </Typography>
              </Alert>
            )}

            {ambiguousHeaders.length > 0 && (
              <Alert severity="error" sx={{ mb: 2 }}>
                <AlertTitle sx={{ fontWeight: 700 }}>
                  {ambiguousHeaders.length} header(s) match more than one field
                </AlertTitle>
                This should not happen with the canonical registry — pick the intended field on the
                rows below. We will not choose for you, because guessing is what wrote the same
                value into two fields in the first place.
              </Alert>
            )}

            {blockers > 0 && (
              <Alert severity="warning" sx={{ mb: 2 }}
                action={(
                  <Button size="small" color="inherit" onClick={createAllRemaining}>
                    Create custom fields for all {blockers}
                  </Button>
                )}
              >
                <AlertTitle sx={{ fontWeight: 700 }}>
                  {blockers} column{blockers === 1 ? '' : 's'} need a decision
                </AlertTitle>
                Map each one to an existing common field (preferred), create a new custom field, or
                exclude it. Nothing is imported or discarded until you choose.
              </Alert>
            )}

            {/* The number that fragments the common field set across clients. */}
            <Alert severity={summary.newCols.length ? 'warning' : 'success'} sx={{ mb: 2 }}
              icon={summary.newCols.length ? undefined : <CheckCircleOutlineIcon fontSize="inherit" />}>
              <AlertTitle sx={{ fontWeight: 700 }}>
                {summary.newCols.length} column{summary.newCols.length === 1 ? '' : 's'} will create
                {' '}NEW custom fields
              </AlertTitle>
              {summary.newCols.length === 0
                ? 'Every column lands on the shared field set — this file stays comparable with every other client.'
                : 'Each new field exists only for this workspace and will not line up with other clients. Map to an existing field where one fits.'}
              {summary.newCols.length > 0 && (
                <Box sx={{ mt: 0.5, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {summary.newCols.map((h) => (
                    <Chip key={h} size="small" variant="outlined" label={`${h} → ${cfKeyFor(h)}`} />
                  ))}
                </Box>
              )}
            </Alert>

            {summary.cfClashes.length > 0 && (
              <Alert severity="error" sx={{ mb: 2 }}>
                <AlertTitle sx={{ fontWeight: 700 }}>Custom field key collision</AlertTitle>
                {summary.cfClashes.map((c) => (
                  <Typography key={`${c.key}-${c.header}-${c.where}`} variant="body2">
                    “{c.header}” normalises to <b>{c.key}</b>, which is already used by “{c.other}”
                    {' '}({c.where}). Same key, two meanings — rename the column or map it to an
                    existing field.
                  </Typography>
                ))}
              </Alert>
            )}

            {summary.collisions.length > 0 && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                More than one column is mapped to {summary.collisions.map(labelFor).join(', ')}.
                The first column with a value wins; the other is not written. Re-point one of them
                if that is not what you want.
              </Alert>
            )}

            {/* Common-field-set picture, before anything is written. */}
            <Box sx={{ mb: 2, p: 1.5, border: '1px solid #e3e9ef', borderRadius: 1, bgcolor: 'background.paper' }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'sectionHeader.main' }}>
                Common field set for this file
              </Typography>
              <Stack direction="row" sx={{ mt: 1, flexWrap: 'wrap', gap: 1 }}>
                <Chip size="small" color="success" variant="outlined"
                  label={`${summary.supplied.size} of ${fields.length} common fields supplied`} />
                <Chip size="small" variant="outlined"
                  label={`${summary.blank.length} not supplied by this file`} />
                {summary.mappedEmpty.size > 0 && (
                  <Tooltip title="Mapped, but every row is empty — reported as not supplied, not as populated.">
                    <Chip size="small" variant="outlined"
                      label={`${summary.mappedEmpty.size} mapped but empty in every row`} />
                  </Tooltip>
                )}
                <Chip size="small" color="warning" variant="outlined"
                  label={`${summary.newCols.length} new custom fields`} />
                {summary.skipped.length > 0 && (
                  <Chip size="small" variant="outlined" label={`${summary.skipped.length} columns excluded`} />
                )}
              </Stack>
              <Typography variant="caption" sx={{ color: '#5C6B7A', display: 'block', mt: 1 }}>
                Blank common fields are fine — clients rarely supply everything. They are listed so
                you know what this file does not cover, not as a failure.
                <Button size="small" onClick={() => setShowBlank((v) => !v)} sx={{ textTransform: 'none' }}>
                  {showBlank ? 'hide' : 'show'} the {summary.blank.length} not supplied
                </Button>
              </Typography>
              {showBlank && (
                <Box sx={{ mt: 0.5, maxHeight: 130, overflowY: 'auto', display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {summary.blank.map((f) => (
                    <Chip key={f.key} size="small" label={f.label}
                      sx={{ bgcolor: '#eceff1', color: '#5C6B7A' }} />
                  ))}
                </Box>
              )}
            </Box>

            {/* Source-cloud tags -> real Primordia tags. Additive: the raw
                columns still map/import exactly as decided in the table below;
                the box only appears when a tag-shaped column exists. */}
            {tagSummary && (
              <Box sx={{ mb: 2, p: 1.5, border: '1px solid #e3e9ef', borderRadius: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'sectionHeader.main' }}>
                  Tags detected
                </Typography>
                <FormControlLabel
                  control={(
                    <Checkbox size="small" checked={importSourceTags}
                      onChange={(e) => setImportSourceTags(e.target.checked)} />
                  )}
                  label={(
                    <Typography variant="caption" sx={{ fontWeight: 600 }}>
                      Import source tags as tags — recognized tag columns become real Primordia tags on
                      each record; the raw columns still import exactly as mapped above
                    </Typography>
                  )}
                />
                <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                  {tagSummary.columns.map((c) => (
                    <Chip
                      key={c.header} size="small" variant="outlined"
                      label={c.kind === 'packed' ? `${c.header} — packed key:value pairs` : `${c.header} → group “${c.group}”`}
                    />
                  ))}
                </Stack>
                {tagSummary.reserved.length > 0 && (
                  <Typography variant="caption" sx={{ color: '#5C6B7A', display: 'block', mt: 0.5 }}>
                    Ignored as AWS system tags (reserved “aws:” prefix):{' '}
                    {tagSummary.reserved.map((r) => r.header).join(', ')}.
                  </Typography>
                )}
                {tagSummary.distinctPairs > 0 && (
                  <>
                    <Typography variant="caption" sx={{ color: '#5C6B7A', display: 'block', mt: 0.5 }}>
                      {tagSummary.rowsWithTags} of {tagSummary.rowCount} row(s) carry tags —{' '}
                      {tagSummary.distinctGroups} distinct group(s), {tagSummary.distinctPairs} distinct
                      (group, value) pair(s).
                    </Typography>
                    <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                      {tagSummary.groups.slice(0, 8).map((g) => (
                        <Chip key={g.group} size="small"
                          sx={{ bgcolor: 'chipMuted.main', color: 'sectionHeader.main', fontWeight: 600 }}
                          label={`${g.group} — ${g.rows} row(s), ${g.values} value(s)`} />
                      ))}
                      {tagSummary.groups.length > 8 && (
                        <Typography variant="caption" sx={{ color: '#5C6B7A', alignSelf: 'center' }}>
                          …and {tagSummary.groups.length - 8} more group(s) — every group is imported,
                          only this list is capped.
                        </Typography>
                      )}
                    </Stack>
                  </>
                )}
                {(tagSummary.truncatedGroups > 0 || tagSummary.truncatedValues > 0) && (
                  <Typography variant="caption" sx={{ color: '#B26A00', display: 'block', mt: 0.5 }}>
                    Length caps applied: {tagSummary.truncatedGroups} group name(s) truncated to 40
                    characters, {tagSummary.truncatedValues} value(s) to 120.
                  </Typography>
                )}
                {tagSummary.reservedSkipped > 0 && (
                  <Typography variant="caption" sx={{ color: '#5C6B7A', display: 'block', mt: 0.5 }}>
                    {tagSummary.reservedSkipped} pair(s) with the reserved “aws:” key prefix were
                    skipped inside packed cells.
                  </Typography>
                )}
                {tagSummary.unparsedPacked.map((s) => (
                  <Alert key={s.header} severity="info" sx={{ mt: 1 }}>
                    Column “{s.header}” was found but no key:value pairs were recognized in its{' '}
                    {s.nonEmptyCells} non-empty cell(s) — the values are kept as whatever the column
                    maps to above (a field or a custom field), nothing becomes a tag.
                  </Alert>
                ))}
                <Typography variant="caption" sx={{ color: '#5C6B7A', display: 'block', mt: 0.5 }}>
                  {importSourceTags
                    ? 'Tags are written onto created and updated records, merged with any tags a record already carries. Unchanged rows are not rewritten just to add tags.'
                    : 'Off — this import behaves exactly as before: these columns land as text only, no tags are written.'}
                </Typography>
              </Box>
            )}

            {/* What makes two rows the same record. Chosen explicitly, checked
                for collisions before it can be used. */}
            <Box sx={{ mb: 2, p: 1.5, border: '1px solid #e3e9ef', borderRadius: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'sectionHeader.main' }}>
                Identity — what makes a row the same application we already have
              </Typography>
              <Typography variant="caption" sx={{ color: '#5C6B7A', display: 'block', mb: 1 }}>
                Rows that match an existing record are UPDATED, never added a second time. Names are
                compared with whitespace collapsed, so “EPIC PRD 01” and “EPIC&nbsp; PRD 01 ” are the
                same application.
              </Typography>
              <Stack direction="row" spacing={2} sx={{ flexWrap: 'wrap', gap: 1 }}>
                <TextField
                  select size="small" label="Match records on" value={strategy}
                  onChange={(e) => setStrategy(e.target.value)} sx={{ minWidth: 260 }}
                >
                  {IDENTITY_STRATEGIES.map((s) => (
                    <MenuItem key={s.key} value={s.key}>{s.label}</MenuItem>
                  ))}
                </TextField>
                {needsIdentityCol && (
                  <TextField
                    select size="small" label={`Column carrying the ${strategyDef.label}`}
                    value={identityCol} onChange={(e) => setIdentityCol(e.target.value)}
                    error={identityMissing} sx={{ minWidth: 260 }}
                  >
                    <MenuItem value=""><em>choose a column</em></MenuItem>
                    {headers.map((h) => <MenuItem key={h} value={h}>{h}</MenuItem>)}
                  </TextField>
                )}
              </Stack>
              <Typography variant="caption" sx={{ color: '#5C6B7A', display: 'block', mt: 1 }}>
                {strategyDef.help}
              </Typography>

              {identityMissing && (
                <Alert severity="warning" sx={{ mt: 1 }}>
                  Point this strategy at a column. Without it there is no key to match on, and every
                  row would fall back to its name.
                </Alert>
              )}

              <Stack direction="row" sx={{ mt: 1, flexWrap: 'wrap', gap: 1 }}>
                <Chip size="small" variant="outlined"
                  label={`${keyCollisions.distinct} distinct keys in ${keyCollisions.total} rows`} />
                <Chip size="small" variant="outlined"
                  color={keyCollisions.blank ? 'warning' : 'default'}
                  label={`${keyCollisions.blank} rows have no key`} />
                <Chip size="small" variant="outlined"
                  color={keyCollisions.collisions.length ? 'error' : 'success'}
                  label={`${keyCollisions.collisions.length} collisions`} />
              </Stack>

              {keyCollisions.blank > 0 && (
                <Typography variant="caption" sx={{ color: '#5C6B7A', display: 'block', mt: 0.5 }}>
                  Rows with no key fall back to their name; a row with neither is skipped and listed
                  in the plan below.
                </Typography>
              )}

              {keyCollisions.collisions.length > 0 && (
                <Alert severity="error" sx={{ mt: 1 }}>
                  <AlertTitle sx={{ fontWeight: 700 }}>
                    This strategy would merge {keyCollisions.collisions.length} pair
                    {keyCollisions.collisions.length === 1 ? '' : 's'} of DIFFERENT records
                  </AlertTitle>
                  <Typography variant="body2">
                    One key covers two differently-named records. Merging two real applications into
                    one is worse than a duplicate — it destroys data. Pick another strategy unless
                    you know these are genuinely the same thing.
                  </Typography>
                  <Box sx={{ mt: 0.5, maxHeight: 120, overflowY: 'auto' }}>
                    {keyCollisions.collisions.slice(0, 25).map((c, idx) => (
                      <Typography key={`${c.key}-${idx}`} variant="caption" sx={{ display: 'block' }}>
                        <b>{c.key}</b> → “{c.a}” and “{c.b}”
                      </Typography>
                    ))}
                    {keyCollisions.collisions.length > 25 && (
                      <Typography variant="caption" sx={{ display: 'block', color: '#5C6B7A' }}>
                        …and {keyCollisions.collisions.length - 25} more.
                      </Typography>
                    )}
                  </Box>
                  <FormControlLabel
                    sx={{ mt: 0.5 }}
                    control={(
                      <Checkbox size="small" checked={ackCollisions}
                        onChange={(e) => setAckCollisions(e.target.checked)} />
                    )}
                    label={(
                      <Typography variant="caption" sx={{ fontWeight: 700 }}>
                        I have reviewed these pairs and they are the same record — use this strategy
                        anyway
                      </Typography>
                    )}
                  />
                </Alert>
              )}
            </Box>

            <Table size="small" sx={{ mt: 1 }}>
              <TableHead>
                <TableRow sx={{ '& th': { fontWeight: 700, color: 'sectionHeader.main' } }}>
                  <TableCell>Column in file</TableCell>
                  <TableCell>Sample value</TableCell>
                  <TableCell sx={{ minWidth: 320 }}>Maps to field</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {headers.map((h) => {
                  const stat = columnStats[h] || { filled: 0, sample: '' };
                  const undecided = !mapping[h];
                  return (
                    <TableRow key={h} sx={undecided ? { bgcolor: '#fffaf2' } : undefined}>
                      <TableCell sx={{ fontWeight: 600, verticalAlign: 'top' }}>
                        {h}
                        <Typography variant="caption" sx={{ display: 'block', color: '#5C6B7A', fontWeight: 400 }}>
                          {stat.filled} of {rows.length} rows have a value
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ color: '#5C6B7A', maxWidth: 220, verticalAlign: 'top', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {stat.sample}
                      </TableCell>
                      <TableCell sx={{ verticalAlign: 'top' }}>{renderTargetCell(h)}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            {/* The dry run. Nothing is written until these numbers are confirmed. */}
            <Box sx={{
              mt: 2, p: 1.5, borderRadius: 1, bgcolor: 'background.paper',
              border: `2px solid ${armed ? '#0D5B9A' : '#e3e9ef'}`,
            }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'sectionHeader.main' }}>
                What this import will do — dry run, nothing has been written yet
              </Typography>
              {!nameCol ? (
                <Alert severity="info" sx={{ mt: 1 }}>
                  Map one column to “Name” first — it is what identifies each application, so until
                  it is set there is nothing to plan.
                </Alert>
              ) : (
                <>
                  <Stack direction="row" sx={{ mt: 1, flexWrap: 'wrap', gap: 1 }}>
                    {planStat('create', 'will be CREATED', plan.create.length, '#0D5B9A', true)}
                    {planStat('update', 'will be UPDATED', plan.update.length, 'sectionHeader.main', true)}
                    {planStat('unchanged', 'unchanged', plan.unchanged.length, '#5C6B7A', true)}
                    {planStat('skipped', 'skipped', plan.skipped.length, '#B26A00', true)}
                  </Stack>
                  <Typography variant="caption" sx={{ color: '#5C6B7A', display: 'block', mt: 1 }}>
                    Matching on {strategyDef.label}
                    {identityCol ? ` from “${identityCol}”` : ''}, falling back to the record name.
                    {' '}Updates overwrite only the fields this file supplies; fields it does not
                    cover are left alone.
                  </Typography>
                  {skipReasons.length > 0 && (
                    <Box sx={{ mt: 0.5, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {skipReasons.map(([reason, n]) => (
                        <Chip key={reason} size="small" variant="outlined" color="warning"
                          label={`${n} × ${reason}`} />
                      ))}
                    </Box>
                  )}
                  <Collapse in={!!expand.create}>{renderPlanRows(plan.create, 'create')}</Collapse>
                  <Collapse in={!!expand.update}>{renderPlanRows(plan.update, 'update')}</Collapse>
                  <Collapse in={!!expand.unchanged}>{renderPlanRows(plan.unchanged, 'unchanged')}</Collapse>
                  <Collapse in={!!expand.skipped}>{renderPlanRows(plan.skipped, 'skipped')}</Collapse>
                  {armed && (
                    <Alert severity="info" sx={{ mt: 1 }}>
                      Plan reviewed. Confirm below to write {plan.create.length} new and
                      {' '}{plan.update.length} updated record
                      {plan.create.length + plan.update.length === 1 ? '' : 's'} into {tenantId}.
                      {' '}Changing anything above re-plans and asks again.
                    </Alert>
                  )}
                </>
              )}
            </Box>

            {busy && <LinearProgress variant="determinate" value={progress} sx={{ mt: 2 }} />}
          </>
        )}
        {renderHistory()}
        </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>{done ? 'Close' : 'Cancel'}</Button>
        {!isMasterWorkspace && !!rows.length && !done && !armed && (
          <Button variant="outlined" onClick={() => setArmed(true)}
            disabled={busy || blockers > 0 || !canWrite}
            sx={{ borderColor: '#0D5B9A', color: '#0D5B9A', textTransform: 'none' }}>
            Review the plan — nothing is written yet
          </Button>
        )}
        {!isMasterWorkspace && !!rows.length && !done && armed && (
          <Button variant="contained" onClick={runImport}
            disabled={busy || blockers > 0 || !canWrite}
            sx={{ bgcolor: '#0D5B9A', '&:hover': { bgcolor: '#021F4B' } }}>
            {busy
              ? `Writing ${progress}%`
              : `Confirm: create ${plan.create.length}, update ${plan.update.length}`}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
